import type { WindowFabricColor } from "@/store/selection";
import type { Product, ProductType } from "@/models/product";
import type {
    SunProtectionOrder,
    SunProtectionOrderProduct,
} from "../order.types";

export type SelectionSnapshot = {
    selectedProductIds: string[];
    windowSelections: Record<string, WindowFabricColor>;
};

export type HydratedSelection = {
    selectedCategory: ProductType;
    selectedTypeId: string;
    activeSnapshot: SelectionSnapshot;
    cachedByCombo: Record<string, SelectionSnapshot>;
    lastTypeIdByCategory: Partial<Record<ProductType, string>>;
};

const comboKey = (category: ProductType, typeId: string) =>
    `${category}:${typeId}`;

// Maps a backend order + the CMS products for the active category into the
// shape `useSelectionStore` expects. Returns null when the order has no
// products yet (caller should bail). Only the active combo gets full color
// metadata; cached combos store raw ids and rely on ConfirmStep's CMS fallback.
export function hydrateSelectionFromOrder(
    order: SunProtectionOrder,
    activeCategoryProducts: Product[],
): HydratedSelection | null {
    if (order.products.length === 0) return null;
    if (activeCategoryProducts.length === 0) return null;

    const groups = new Map<string, SunProtectionOrderProduct[]>();
    for (const p of order.products) {
        const key = comboKey(p.productType, p.sunProtectionProductId);
        const arr = groups.get(key) ?? [];
        arr.push(p);
        groups.set(key, arr);
    }

    const first = order.products[0];
    const activeKey = comboKey(first.productType, first.sunProtectionProductId);
    const cache: Record<string, SelectionSnapshot> = {};
    const lastType: Partial<Record<ProductType, string>> = {};
    let activeSnapshot: SelectionSnapshot = {
        selectedProductIds: [],
        windowSelections: {},
    };

    for (const [key, items] of groups) {
        // The grouping key is `${productType}:${sunProtectionProductId}` —
        // derive both halves from the zod-validated order product instead of
        // re-parsing the string (which would re-introduce an `as ProductType`
        // cast for the category half).
        const category = items[0].productType;
        const typeId = items[0].sunProtectionProductId;
        const productSrc =
            key === activeKey
                ? activeCategoryProducts.find((p) => p.id === typeId)
                : null;
        const ids: string[] = [];
        const sels: Record<string, WindowFabricColor> = {};
        for (const op of items) {
            ids.push(op.productId);
            const fabric = productSrc?.fabricColors.find(
                (c) => c.id === op.colorId,
            );
            const frame = productSrc?.frameColors.find(
                (c) => c.id === op.frameColorId,
            );
            sels[op.productId] = {
                windowId: op.productId,
                fabricType: fabric?.type ?? "TRANSLUCENT",
                colorId: op.colorId,
                colorHex: fabric?.hexColor ?? null,
                colorName: fabric?.name ?? null,
                pricePerCm2: fabric?.price ?? null,
                frameColorId: op.frameColorId,
                frameColorName: frame?.name ?? null,
                frameColorHex: frame?.hexColor ?? null,
                controlType: op.controlType,
            };
        }
        const snapshot: SelectionSnapshot = {
            selectedProductIds: ids,
            windowSelections: sels,
        };
        lastType[category] = typeId;
        if (key === activeKey) activeSnapshot = snapshot;
        else cache[key] = snapshot;
    }

    return {
        selectedCategory: first.productType,
        selectedTypeId: first.sunProtectionProductId,
        activeSnapshot,
        cachedByCombo: cache,
        lastTypeIdByCategory: lastType,
    };
}
